/**
 * InterStopsImportDialog — import Excel des inter-arrêts (Arrêt origine | Arrêt destination | Distance km | Temps min).
 * Rien n'est créé sans lignes NEW réelles : CONFLICT / DUPLICATE / INVALID ne sont jamais écrits.
 */
import React, { useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ActionButton, StatusBadge } from "@/components/ds";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { FileSpreadsheet, Upload, Download } from "lucide-react";
import { formatKm, formatMinutes } from "@/lib/stopsUnits";

const FIELD = "w-full border border-zinc-900 px-2 py-1.5 text-sm bg-white";
const LABEL = "block text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1";
const COLS = [["origin_col", "Arrêt origine"], ["destination_col", "Arrêt destination"], ["distance_col", "Distance km"], ["time_col", "Temps min"]];
const STATUSES = ["NEW", "DUPLICATE", "CONFLICT", "INVALID"];
const TONE = { NEW: "success", DUPLICATE: "neutral", CONFLICT: "warning", INVALID: "danger" };
const LBL = { NEW: "Nouveau", DUPLICATE: "Doublon", CONFLICT: "Conflit à valider", INVALID: "Invalide" };

export async function downloadInterStopsTemplate() {
  try {
    const r = await api.get("/inter-stops/import/template.xlsx", { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a"); a.href = url; a.download = "LOGIBUS_modele_inter_arrets.xlsx";
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Téléchargement impossible"); }
}

export default function InterStopsImportDialog({ open, onClose, onImported }) {
  const [file, setFile] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [sheet, setSheet] = useState("");
  const [mapping, setMapping] = useState({});
  const [preview, setPreview] = useState(null);
  const [filter, setFilter] = useState("ALL");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const sheetInfo = useMemo(() => analysis?.sheets.find((s) => s.name === sheet), [analysis, sheet]);
  const complete = COLS.every(([k]) => mapping[k] != null);

  const close = () => { setFile(null); setAnalysis(null); setSheet(""); setMapping({}); setPreview(null); setResult(null); setFilter("ALL"); onClose(); };
  const fd = () => { const f = new FormData(); f.append("file", file); f.append("sheet", sheet); f.append("mapping", JSON.stringify(mapping)); return f; };
  const run = async (fn) => { setBusy(true); try { await fn(); } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); } finally { setBusy(false); } };

  const analyze = (f) => run(async () => {
    setFile(f); setPreview(null); setResult(null);
    const form = new FormData(); form.append("file", f);
    const { data } = await api.post("/inter-stops/import/analyze", form);
    setAnalysis(data);
    const first = data.sheets.find((s) => s.row_count > 0) || data.sheets[0];
    setSheet(first?.name || ""); setMapping(first?.suggested_mapping || {});
  });
  const doPreview = () => run(async () => { const { data } = await api.post("/inter-stops/import/preview", fd()); setPreview(data); setFilter("ALL"); });
  const doApply = () => run(async () => {
    const { data } = await api.post("/inter-stops/import/apply", fd());
    setResult(data); toast.success(`${data.created} inter-arrêt(s) importé(s)`); onImported && onImported();
  });

  const rows = preview ? preview.rows.filter((r) => filter === "ALL" || r.status === filter) : [];

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-4xl border border-zinc-900 rounded-none p-0" data-testid="inter-stops-import-dialog">
        <DialogHeader className="bg-zinc-900 text-white px-4 py-3">
          <DialogTitle className="font-display uppercase tracking-tight flex items-center gap-2"><FileSpreadsheet size={16} className="text-[#FFC60A]" /> Importer des inter-arrêts depuis Excel</DialogTitle>
          <DialogDescription className="text-zinc-400 text-xs">
            Colonnes attendues : Arrêt origine · Arrêt destination · Distance km · Temps min. Origine/destination = noms exacts du référentiel (aucun arrêt créé). A→B et B→A sont deux liaisons distinctes.
          </DialogDescription>
        </DialogHeader>
        <div className="p-4 space-y-3">
          <div className="flex flex-wrap items-end gap-3">
            <div><label className={LABEL}>1 · Fichier .xlsx</label>
              <input type="file" accept=".xlsx" data-testid="inter-import-file-input" disabled={busy} className="text-sm" onChange={(e) => e.target.files?.[0] && analyze(e.target.files[0])} /></div>
            <ActionButton variant="ghost" size="sm" icon={Download} onClick={downloadInterStopsTemplate} testId="inter-import-template-btn">Modèle Excel + arrêts disponibles</ActionButton>
          </div>
          {analysis && (
            <div className="border border-zinc-300 p-3 space-y-2 bg-zinc-50" data-testid="inter-import-analysis">
              <div className="text-xs"><b>{analysis.filename}</b> · {analysis.sheets.map((s) => `${s.name} (${s.row_count} lignes)`).join(", ")}</div>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                <div><label className={LABEL}>2 · Feuille</label>
                  <select data-testid="inter-import-sheet" className={FIELD} value={sheet} onChange={(e) => { setSheet(e.target.value); setMapping(analysis.sheets.find((s) => s.name === e.target.value)?.suggested_mapping || {}); setPreview(null); }}>
                    {analysis.sheets.map((s) => <option key={s.name} value={s.name}>{s.name}</option>)}
                  </select></div>
                {COLS.map(([k, l]) => (
                  <div key={k}><label className={LABEL}>{l} *</label>
                    <select data-testid={`inter-import-map-${k}`} className={FIELD} value={mapping[k] ?? ""} onChange={(e) => setMapping((m) => ({ ...m, [k]: e.target.value === "" ? null : Number(e.target.value) }))}>
                      <option value="">— choisir —</option>
                      {(sheetInfo?.headers || []).map((h, i) => <option key={i} value={i}>{h}</option>)}
                    </select></div>
                ))}
              </div>
              <ActionButton variant="dark" onClick={doPreview} loading={busy} disabled={!complete} testId="inter-import-preview-btn">3 · Analyser les lignes</ActionButton>
            </div>
          )}
          {preview && !result && (
            <div className="space-y-2">
              <div className="flex flex-wrap gap-1" data-testid="inter-import-counts">
                {["ALL", ...STATUSES].map((k) => (
                  <button key={k} type="button" onClick={() => setFilter(k)} data-testid={`inter-import-filter-${k}`}
                    className={`px-2 py-1 text-[10px] font-mono uppercase tracking-widest border border-zinc-900 ${filter === k ? "bg-zinc-900 text-[#FFC60A]" : "bg-white"}`}>
                    {k === "ALL" ? `Tout ${preview.row_count}` : `${LBL[k]} ${preview.counts[k]}`}
                  </button>
                ))}
              </div>
              <div className="max-h-[300px] overflow-auto border border-zinc-300">
                <table className="w-full text-xs">
                  <thead className="bg-zinc-900 text-white sticky top-0"><tr>{["L.", "Origine", "Destination", "Distance", "Temps", "Statut", "Détail"].map((h) => <th key={h} className="px-2 py-1 text-left font-mono text-[10px]">{h}</th>)}</tr></thead>
                  <tbody data-testid="inter-import-preview-body">
                    {rows.map((r) => (
                      <tr key={r.row} className="border-t border-zinc-200" data-testid={`inter-import-row-${r.row}`}>
                        <td className="px-2 py-1 font-mono text-zinc-500">{r.row}</td>
                        <td className="px-2 py-1">{r.origin_name || <em className="text-red-600">{r.origin_text || "—"}</em>}</td>
                        <td className="px-2 py-1">{r.destination_name || <em className="text-red-600">{r.destination_text || "—"}</em>}</td>
                        <td className="px-2 py-1 font-mono">{r.distance_meters != null ? formatKm(r.distance_meters) : String(r.distance_km ?? "—")}</td>
                        <td className="px-2 py-1 font-mono">{r.travel_time_seconds != null ? formatMinutes(r.travel_time_seconds) : String(r.time_min ?? "—")}</td>
                        <td className="px-2 py-1"><StatusBadge tone={TONE[r.status]} label={LBL[r.status]} size="sm" /></td>
                        <td className="px-2 py-1 text-zinc-600">{r.reason || "→ importé"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {result && (
            <div className="border border-emerald-600 bg-emerald-50 p-3 text-sm" data-testid="inter-import-result">
              Lignes lues <b>{result.rows_read}</b> · importées <b data-testid="inter-import-result-created">{result.created}</b> · doublons <b>{result.counts.DUPLICATE}</b> · conflits à valider <b>{result.counts.CONFLICT}</b> · invalides <b>{result.counts.INVALID}</b> · valeur choisie automatiquement : <b>NON</b>
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2 border-t border-zinc-200">
            <ActionButton variant="ghost" onClick={close} testId="inter-import-close">{result ? "Fermer" : "Annuler"}</ActionButton>
            {preview && !result && <ActionButton variant="primary" icon={Upload} onClick={doApply} loading={busy} disabled={preview.counts.NEW === 0} testId="inter-import-apply-btn">4 · Importer {preview.counts.NEW} liaison(s)</ActionButton>}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
